import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A page filter kept in the URL (shareable, survives refresh and back/forward).
 * The default value is not written, so URLs stay clean.
 */
export function useUrlState<T extends string | number>(
  key: string,
  defaultValue: T,
  parse: (raw: string | null) => T = raw =>
    raw === null
      ? defaultValue
      : typeof defaultValue === 'number'
      ? (Number(raw) as T)
      : (raw as T),
): [T, (value: T, options?: { reset?: string[] }) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = parse(searchParams.get(key));
  const setValue = useCallback(
    (next: T, options?: { reset?: string[] }) =>
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          // `reset` clears dependent params in the same navigation (e.g. tab when the cluster changes).
          options?.reset?.forEach(other => params.delete(other));
          if (next === defaultValue || next === '') params.delete(key);
          else params.set(key, String(next));
          return params;
        },
        { replace: true },
      ),
    [key, defaultValue, setSearchParams],
  );
  return [value, setValue];
}
